import { type MarkupProfileDetail } from '@utazzo/shared';
import { UzBadge, UzButton } from '@utazzo/ui';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import React, { useState } from 'react';

import { MarkupProfileFormDrawer } from '../components/MarkupProfileFormDrawer';
import { getMarkupProfile } from '../markup-profiles.api';
import { useDeleteMarkupProfile, useMarkupProfilesList } from '../use-markup-profiles';

/**
 * Markup profiles master (Pricing → Markup Profiles). The quote-engine markup
 * master: one Global default + per-destination-city rows, each with per-category
 * rules applied during itinerary pricing.
 */
export const MarkupProfilesListPage = (): React.JSX.Element => {
  const list = useMarkupProfilesList({ page: 1, pageSize: 100, sortDir: 'asc' });
  const del = useDeleteMarkupProfile();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [mode, setMode] = useState<'create' | 'edit'>('create');
  const [editing, setEditing] = useState<MarkupProfileDetail | null>(null);

  const openCreate = (): void => {
    setEditing(null);
    setMode('create');
    setDrawerOpen(true);
  };
  const openEdit = async (id: string): Promise<void> => {
    const detail = await getMarkupProfile(id);
    setEditing(detail);
    setMode('edit');
    setDrawerOpen(true);
  };

  const rows = list.data?.data ?? [];

  return (
    <div className="mx-auto max-w-5xl space-y-5 p-6">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-lg font-semibold tracking-tight text-zinc-900">Markup profiles</h1>
          <p className="mt-0.5 text-sm text-zinc-500">
            Per-category markup applied to itinerary quotes. Destination profiles override the
            global default per city.
          </p>
        </div>
        <UzButton onClick={openCreate}>
          <Plus className="mr-1.5 h-4 w-4" aria-hidden />
          Add profile
        </UzButton>
      </header>

      {/* Phones: the table keeps its natural column widths and scrolls sideways
          inside the card instead of squeezing / clipping the columns. */}
      <div className="overflow-hidden rounded-lg border border-zinc-200 bg-white max-sm:overflow-x-auto">
        <table className="w-full text-sm max-sm:w-max max-sm:min-w-full max-sm:whitespace-nowrap">
          <thead>
            <tr className="border-b border-zinc-100 text-left text-xs font-semibold tracking-wider text-zinc-500 uppercase">
              <th className="px-4 py-2.5">Name</th>
              <th className="px-4 py-2.5">Scope</th>
              <th className="px-4 py-2.5">City</th>
              <th className="px-4 py-2.5">Rules</th>
              <th className="px-4 py-2.5">Status</th>
              <th className="px-4 py-2.5" />
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-100">
            {rows.length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-sm text-zinc-400">
                  {list.isLoading ? 'Loading...' : 'No markup profiles yet.'}
                </td>
              </tr>
            )}
            {rows.map((p) => (
              <tr key={p.id} className="hover:bg-zinc-50">
                <td className="px-4 py-2.5 font-medium text-zinc-900">{p.name}</td>
                <td className="px-4 py-2.5">
                  <UzBadge tone={p.scope === 'GLOBAL' ? 'secondary' : 'default'}>
                    {p.scope === 'GLOBAL' ? 'Global' : 'Destination'}
                  </UzBadge>
                </td>
                <td className="px-4 py-2.5 text-zinc-600">{p.cityName ?? '-'}</td>
                <td className="px-4 py-2.5 text-zinc-600 tabular-nums">{p.ruleCount}</td>
                <td className="px-4 py-2.5">
                  {p.isActive ? (
                    <UzBadge tone="success">Active</UzBadge>
                  ) : (
                    <UzBadge tone="secondary">Inactive</UzBadge>
                  )}
                </td>
                <td className="px-4 py-2.5">
                  <div className="flex items-center justify-end gap-1">
                    <button
                      type="button"
                      onClick={() => void openEdit(p.id)}
                      className="inline-flex h-7 w-7 items-center justify-center rounded text-zinc-500 hover:bg-zinc-100 hover:text-zinc-900"
                      aria-label="Edit"
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </button>
                    {p.scope !== 'GLOBAL' && (
                      <button
                        type="button"
                        onClick={() => {
                          if (confirm(`Delete markup profile "${p.name}"?`)) del.mutate(p.id);
                        }}
                        className="inline-flex h-7 w-7 items-center justify-center rounded text-zinc-500 hover:bg-red-50 hover:text-red-600"
                        aria-label="Delete"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <MarkupProfileFormDrawer
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
        mode={mode}
        profile={editing}
      />
    </div>
  );
};
